import { useMemo, useState } from 'react';
import { Box, Button, Text } from '@backstage/ui';
import { toastApiRef, ToastApi, useApi } from '@backstage/frontend-plugin-api';
import { useAsyncEntity, useEntity } from '@backstage/plugin-catalog-react';
import {
  AccountEntity,
  CloudToolsError,
  matchesMutedScanError,
  MutedScanError,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ProvidersApi,
  providersApiRef,
} from '@internal/backstage-plugin-catalog-devops-react';
import { AccountApi, accountApiRef } from '../../api';
import { buildAccountPayload, useIsAccountAdmin } from '../SettingsPages';
import styles from '../resources-page.module.css';

const SCOPE_BADGE_CLASS: Record<string, string> = {
  Region: styles.badgeRegion,
  Account: styles.badgeAccount,
  Resource: styles.badgeResource,
};

export interface ScanErrorsListProps {
  /** Opens the same confirm/credential flow as the header scan button.
   * Rendered only when provided (account pages have the account context). */
  onStartScan?: () => void;
}

// ResourceIds holds the affected entities whatever the scope: region names
// for Region-scoped actions, account ids for Account-scoped ones, resource
// ids for Resource-scoped ones — label them accordingly.
const scopeNoun = (scope: string): string => {
  if (scope === 'Region') return 'region';
  if (scope === 'Account') return 'account';
  return 'resource';
};

const scopeIdLabel = (scope: string, count: number): string => {
  if (scope === 'Region') return count === 1 ? 'region' : 'regions';
  if (scope === 'Account') return count === 1 ? 'account' : 'accounts';
  return count === 1 ? 'resource ID' : 'resource IDs';
};

/**
 * Scan error cards styled after the design mockup: one card per failed
 * inventory action — scope badge, action name, monospace message on the
 * danger surface and affected resource counts. Admins can mute/unmute each
 * error; muted cards are dimmed and stop triggering the warnings.
 */
export function ScanErrorsList(props: Readonly<ScanErrorsListProps>) {
  const { onStartScan } = props;
  const { entity } = useEntity();
  const account = entity as AccountEntity;
  const { refresh } = useAsyncEntity();

  const accountApi = useApi<AccountApi>(accountApiRef);
  const providersApi = useApi<ProvidersApi>(providersApiRef);
  const toastApi = useApi<ToastApi>(toastApiRef);

  const isAdmin = useIsAccountAdmin(account);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const errors: CloudToolsError[] = account.spec?.errors ?? [];
  const mutedErrors: MutedScanError[] = account.spec?.mutedErrors ?? [];

  const isMuted = (error: CloudToolsError) =>
    mutedErrors.some(rule => matchesMutedScanError(error, rule));

  const updateMutedErrors = async (muted: MutedScanError[]) => {
    setIsSaving(true);
    try {
      const response = await accountApi.editAccountEntity(
        account.spec.dynamic_uuid,
        buildAccountPayload(account, { MutedErrors: muted }),
      );
      if (response.succeeded) {
        // Re-sync so spec.mutedErrors updates on the entity, then refresh.
        await providersApi.triggerProvider('Account');
        refresh?.();
      } else {
        toastApi.post({
          title: 'Failed to update muted scan errors.',
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }
    } catch (error) {
      toastApi.post({
        title: 'Failed to update muted scan errors.',
        description: (error as Error).message,
        status: 'danger',
        timeout: 10000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const muteError = (error: CloudToolsError) =>
    updateMutedErrors([
      ...mutedErrors,
      { actionName: error.ActionName, scope: error.Scope },
    ]);

  const unmuteError = (error: CloudToolsError) =>
    updateMutedErrors(
      mutedErrors.filter(rule => !matchesMutedScanError(error, rule)),
    );

  const mutedCount = useMemo(
    () => errors.filter(isMuted).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors, mutedErrors],
  );

  if (!errors.length) return null;

  const checkEntries = errors.reduce(
    (sum, error) => sum + (error.ResourceIds?.length || 1),
    0,
  );

  return (
    <>
      <Text as="p" className={styles.errIntro}>
        {errors.length} inventory{' '}
        {errors.length === 1 ? 'action' : 'actions'} failed ({checkEntries}{' '}
        region/account/resource checks) during the last scan. Affected
        resources keep their last known inventory data until a scan succeeds.
      </Text>
      <Box>
        {errors.map((error, index) => {
          const muted = isMuted(error);
          return (
            <Box
              key={`${error.ActionName}-${index}`}
              className={`${styles.errRow}${muted ? ` ${styles.errRowMuted}` : ''}`}
            >
              <Box className={styles.errHead}>
                <span
                  className={`${styles.badge} ${
                    SCOPE_BADGE_CLASS[error.Scope] ?? styles.badgeNeutral
                  }`}
                >
                  {error.Scope}
                </span>
                <span className={styles.errAction}>{error.ActionName}</span>
                {isAdmin && (
                  <Box style={{ marginLeft: 'auto' }}>
                    <Button
                      variant="tertiary"
                      size="small"
                      onPress={() =>
                        muted ? unmuteError(error) : muteError(error)
                      }
                      isDisabled={isSaving}
                    >
                      {muted ? 'Unmute' : 'Mute'}
                    </Button>
                  </Box>
                )}
              </Box>
              <Box className={styles.errTargets}>
                {error.ResourceType && (
                  <>Resource type: {error.ResourceType} · </>
                )}
                {error.ResourceIds?.length ?? 0}{' '}
                {scopeNoun(error.Scope)}
                {(error.ResourceIds?.length ?? 0) === 1 ? '' : 's'} affected
              </Box>
              <pre className={styles.errMsg}>{error.Message}</pre>
              {error.ResourceIds?.length > 0 && (
                <Box className={styles.errIds}>
                  <span
                    className={styles.chip}
                    title={error.ResourceIds.join(', ')}
                  >
                    <b>{error.ResourceIds.length}</b>{' '}
                    {scopeIdLabel(error.Scope, error.ResourceIds.length)}
                  </span>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Box className={styles.drawerFoot} style={{ marginTop: '16px' }}>
        {isAdmin && onStartScan && (
          <Button variant="primary" size="small" onPress={onStartScan}>
            ↻ Run re-scan
          </Button>
        )}
        <Button variant="tertiary" size="small" onPress={() => refresh?.()}>
          Refresh
        </Button>
        <span className={styles.drawerFootNote}>
          {mutedCount} of {errors.length} muted
        </span>
      </Box>
    </>
  );
}
